/**
 * Serializacion canonica de un asiento encadenado (spec/03-data-model.md §5).
 *
 * Regla: campos en el orden fijo del contrato de columnas, apuntes ordenados
 * por `index`, texto normalizado a NFC, importes como enteros de unidades
 * minimas con su moneda y escala, campos vacios/nulos omitidos (no
 * serializados como cadena vacia: ausentes). La misma entrada produce
 * siempre la misma cadena, en cualquier dispositivo y momento.
 *
 * entry_hash cubre todo el contenido de la fila salvo device_chain_hash (que
 * se calcula a partir de entry_hash, y por tanto no puede formar parte de su
 * propia entrada sin circularidad).
 */

import type { Posting } from "../core/entry.js";

export interface ChainedEntryContent {
  readonly entryId: string;
  readonly deviceId: string;
  readonly deviceSeq: number;
  readonly date: string;
  readonly description: string;
  readonly origin: string;
  readonly reversesEntryId: string | null;
  readonly createdAt: string; // instante UTC ISO-8601
  readonly postings: readonly Posting[];
}

function nfc(s: string): string {
  return s.normalize("NFC");
}

function serializePosting(p: Posting): string {
  const parts = [
    `index=${p.index}`,
    `account=${nfc(p.accountId)}`,
    `amount=${p.amountMinor.toString()}`,
    `currency=${p.currency}`,
    `scale=${p.scale}`,
  ];
  if (p.quantityMinor !== null) parts.push(`qty=${p.quantityMinor.toString()}`);
  if (p.instrumentId !== null) parts.push(`instrument=${nfc(p.instrumentId)}`);
  return parts.join(",");
}

/** Produce la cadena canonica sobre la que se calcula entry_hash. */
export function canonicalizeEntry(content: ChainedEntryContent): string {
  const sortedPostings = [...content.postings].sort((a, b) => a.index - b.index);
  const fields: string[] = [
    `entry_id=${content.entryId}`,
    `device_id=${content.deviceId}`,
    `device_seq=${content.deviceSeq}`,
    `date=${content.date}`,
    `description=${nfc(content.description)}`,
    `origin=${content.origin}`,
  ];
  if (content.reversesEntryId !== null) {
    fields.push(`reverses_entry_id=${content.reversesEntryId}`);
  }
  fields.push(`created_at=${content.createdAt}`);
  fields.push(`postings=[${sortedPostings.map(serializePosting).join(";")}]`);
  return fields.join("|");
}
